import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { DnsEndpoint } from '../types/models/DnsEndpoint';
import type { ApiResponseDnsEndpoints } from '../types/api/ApiResponseDomainName';

interface UseDnsEndpointsReturn {
  endpoints: DnsEndpoint[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * GET /datacenters/domain-names/dns-endpoints — the catalog of PowerDNS endpoints visible to the
 * client (shared + their own), each {id, name, public_nameservers}. Gated by `enabled` so the modal only
 * fetches the catalog when it opens: the fetch runs on mount when `enabled` is already true and
 * whenever `enabled` transitions to true; while `enabled` is false no request is made. `refetch`
 * runs regardless of `enabled` so a modal can offer a "Retry". Failures are JSend `error` at HTTP
 * 200, so the authoritative success check is `status === 'success'`, not `response.ok`.
 */
export const useDnsEndpoints = (enabled: boolean = true): UseDnsEndpointsReturn => {
  const [endpoints, setEndpoints] = useState<DnsEndpoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchDnsEndpoints = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch('/datacenters/domain-names/dns-endpoints');
      const body = (await response.json().catch(() => null)) as ApiResponseDnsEndpoints | null;

      if (!response.ok || body?.status !== 'success' || !body.data?.dns_endpoints) {
        setEndpoints([]);
        setError(body?.message ?? 'Failed to load DNS endpoints');
        return;
      }

      setEndpoints(body.data.dns_endpoints);
      setError(null);
    } catch (err) {
      setEndpoints([]);
      setError(err instanceof Error ? err.message : 'An error occurred while loading DNS endpoints');
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (enabled && auth.isAuthenticated) {
      fetchDnsEndpoints();
    }
  }, [enabled, auth.isAuthenticated, fetchDnsEndpoints]);

  return { endpoints, loading, error, refetch: fetchDnsEndpoints };
};
